import { useEffect, useState } from "react";
import { listSettlements, type Settlement } from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { Banknote, Clock } from "lucide-react";

export function SettlementsPage() {
  const [items, setItems] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listSettlements()
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const columns: ColumnDef<Settlement>[] = [
    {
      key: "period",
      header: "Période",
      minWidth: 130,
      cell: (s) => <span className="text-sm font-semibold text-[#181c32]">{formatDate(s.period_date)}</span>,
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 80,
      cell: (s) => <span className="font-semibold">{s.currency}</span>,
    },
    {
      key: "gross",
      header: "Brut",
      align: "right",
      minWidth: 130,
      cell: (s) => <span>{formatAmount(s.gross_amount, s.currency)}</span>,
    },
    {
      key: "commission",
      header: "Commission",
      align: "right",
      minWidth: 130,
      cell: (s) => <span className="text-warning-700">−{formatAmount(s.commission_amount, s.currency)}</span>,
    },
    {
      key: "refunds",
      header: "Refunds",
      align: "right",
      minWidth: 130,
      cell: (s) => <span className="text-danger-700">−{formatAmount(s.refund_amount, s.currency)}</span>,
    },
    {
      key: "net",
      header: "Net versé",
      align: "right",
      minWidth: 140,
      cell: (s) => <span className="font-bold text-success-700">{formatAmount(s.net_amount, s.currency)}</span>,
    },
    {
      key: "status",
      header: "Statut",
      minWidth: 110,
      cell: (s) => <Badge color={statusColor(s.status)}>{s.status}</Badge>,
    },
    {
      key: "tmb_ref",
      header: "Ref. TMB",
      minWidth: 180,
      cell: (s) => (
        <span className="font-mono text-xs text-[#7e8299]">{s.tmb_reference ?? "—"}</span>
      ),
    },
    {
      key: "settled_at",
      header: "Encaissé le",
      minWidth: 130,
      cell: (s) => <span className="text-xs text-[#7e8299]">{s.settled_at ? formatDate(s.settled_at) : "—"}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Settlements"
        breadcrumbs={[{ label: "Finance" }, { label: "Settlements" }]}
      />

      {items.length === 0 && !loading ? (
        <div className="card">
          <div className="card-body py-16 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-50 text-brand-700 mb-4">
              <Banknote size={28} />
            </div>
            <div className="text-lg font-bold text-[#181c32] mb-2">Settlements bientôt disponibles</div>
            <div className="text-sm text-[#7e8299] max-w-md mx-auto leading-relaxed mb-6">
              Le module Settlements permettra de visualiser les virements quotidiens consolidés
              effectués vers votre compte TMB (J+1 sur les paiements réussis, net de commissions
              et refunds).
            </div>
            <div className="inline-flex items-center gap-2 text-xs text-[#7e8299] font-medium bg-warning-50 text-warning-700 px-3 py-1.5 rounded-full">
              <Clock size={12} />
              Phase 6 — en cours de développement
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left">
              <Feature label="Consolidation journalière" desc="Tous les paiements réussis du jour J regroupés en un seul virement" />
              <Feature label="Net après déductions" desc="Brut moins commissions Moko moins refunds en attente" />
              <Feature label="Virement TMB automatique" desc="Génération de l'ordre de virement vers votre compte bancaire" />
              <Feature label="Réconciliation par CSV" desc="Import CSV TMB pour matcher chaque ligne settlement" />
            </div>
          </div>
        </div>
      ) : (
        <DataTable<Settlement>
          title="Historique des settlements"
          subtitle="Virements consolidés J+1 vers compte TMB"
          columns={columns}
          data={items}
          rowKey={(s) => s.id}
          loading={loading}
        />
      )}
    </div>
  );
}

function Feature({ label, desc }: { label: string; desc: string }) {
  return (
    <div className="rounded-xl bg-[#fcfcfd] border border-[#eff2f5] p-4">
      <div className="font-semibold text-[#181c32] text-sm">{label}</div>
      <div className="text-xs text-[#7e8299] mt-1 leading-relaxed">{desc}</div>
    </div>
  );
}
